/**
 * Server-side structured data for the AvoidForeclosure tool page.
 * Lives in a data module (not the page component) so routeMeta can emit it
 * into the server HTML; react-helmet would only add it after hydration.
 */
export const FAQS = [
  {
    id: "how_many_missed",
    q: "How many payments can I miss before foreclosure starts?",
    a: "Under federal servicing rules, a servicer generally cannot make the first official foreclosure filing until the loan is more than 120 days delinquent, which gives you time to apply for loss mitigation. That is a floor, not a promise — state timelines, judicial versus non-judicial process, and your specific loan all affect what happens and when. A single missed payment already triggers late fees and credit reporting, so treat the first missed payment as the moment to call, not the fourth.",
  },
  {
    id: "forbearance_credit",
    q: "Does forbearance hurt my credit?",
    a: "Forbearance that you formally agree to with your servicer is typically reported as an arrangement rather than as a new delinquency while you follow the plan, but reporting practices vary by servicer and program, and any late payments reported before the plan started remain on your report. Ask your servicer in writing how they will report the account during and after the plan before you accept it.",
  },
  {
    id: "sell_while_behind",
    q: "Can I sell my home while I am behind on payments?",
    a: "Yes. If the sale price covers the loan balance plus costs, a normal sale pays off the mortgage and ends the problem, and you keep any remaining equity. If it does not cover the balance, you would need the servicer's approval for a short sale. Selling before a foreclosure sale date almost always leaves you in a better position than letting the process run.",
  },
  {
    id: "denied_modification",
    q: "What if my loan modification is denied?",
    a: "Ask for the denial in writing with the specific reason. Denials often come from missing documents, an income calculation you can correct, or an investor rule that a different option satisfies. You generally have the right to appeal within a set window after a complete application, and a housing counselor or legal aid attorney can help you decide whether to appeal, reapply after a change in circumstances, or move to a different option.",
  },
  {
    id: "haf",
    q: "Is Homeowner Assistance Fund money still available?",
    a: "It depends entirely on your state. HAF was funded through state programs, and many have fully committed their allocations while others still operate assistance in some form. Check your state housing finance agency's current program page directly rather than relying on an older article, and ask a HUD-approved counselor what is active in your area right now.",
  },
  {
    id: "cost",
    q: "Do I have to pay for foreclosure help?",
    a: "No. HUD-approved housing counseling is free, and your servicer does not charge you to apply for loss mitigation. Anyone asking for an upfront fee to negotiate with your lender, or asking you to sign over your deed, should be treated as a scam until proven otherwise.",
  },
];

  export const AVOID_FORECLOSURE_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Article",
      headline: "How to Avoid Foreclosure if You're Falling Behind on Mortgage Payments",
      description:
        "Immediate steps, loss mitigation options, state help, alternatives, and scam warnings for homeowners behind on their mortgage.",
      url: "https://www.homeapproach.com/tools/avoid-foreclosure",
      author: { "@type": "Organization", name: "Home Approach", url: "https://www.homeapproach.com" },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: "https://www.homeapproach.com",
      },
      isPartOf: {
        "@type": "WebSite",
        name: "Home Approach",
        url: "https://www.homeapproach.com",
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://www.homeapproach.com/" },
        { "@type": "ListItem", position: 2, name: "Tools", item: "https://www.homeapproach.com/tools" },
        {
          "@type": "ListItem",
          position: 3,
          name: "Avoid Foreclosure",
          item: "https://www.homeapproach.com/tools/avoid-foreclosure",
        },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};
